import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Building, Layers } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import MultiStepTenantForm from '../forms/MultiStepTenantForm';
import { getTenant, createTenant, updateTenant } from '../../services/api';
import { TenantFormData, TenantLevel, TenantType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface TenantFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save; use to close parent modals (e.g. detail view). Falls back to onClose. */
  onSaved?: () => void;
  tenantId?: string;
  initialLevel?: TenantLevel;
  initialType?: TenantType;
}

const TenantFormModal: React.FC<TenantFormModalProps> = ({ 
  isOpen, 
  onClose,
  onSaved,
  tenantId,
  initialLevel,
  initialType = TenantType.ORG
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenant, setTenant] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!tenantId;
  
  // Fetch tenant data if in edit mode
  useEffect(() => {
    const fetchTenant = async () => {
      if (!isEditMode || !isOpen) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await getTenant(tenantId!);
        setTenant(data);
      } catch (error: any) {
        console.error('Error fetching tenant:', error);
        let errorMessage = 'Failed to load tenant data. Please try again.';
        
        // Check for specific error messages from the API
        if (error.response) {
          if (error.response.status === 403) {
            errorMessage = 'You do not have permission to access this tenant.';
          } else if (error.response.status === 404) {
            errorMessage = 'Tenant not found. It may have been deleted.';
          } else if (error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTenant();
  }, [tenantId, isEditMode, isOpen]);
  
  // Handle form submission
  const handleSubmit = async (data: TenantFormData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Submitting tenant form
      
      if (isEditMode) {
        await updateTenant(tenantId!, data);
        toast.success('Tenant updated successfully');
      } else {
        await createTenant(data);
        toast.success('Tenant created successfully');
      }

      if (onSaved) {
        onSaved();
      } else {
        onClose();
      }
    } catch (error: any) {
      console.error('Error saving tenant:', error);
      let errorMessage = 'Failed to save tenant. Please try again.';
      
      // Check for specific error messages from the API
      if (error.response && error.response.data) {
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Determine page title based on mode and tenant level
  const getModalTitle = () => {
    if (isEditMode) {
      return `Edit ${tenant?.name || 'Tenant'}`;
    }
    
    if (initialLevel === 'L2' || initialType === 'partner') {
      return 'Add Partner';
    } else if (initialLevel === 'L3' || initialType === 'org') {
      return 'Add Organization';
    }
    
    return 'Add Tenant';
  };
  
  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={getModalTitle()}
        size="xl"
      >
        <div className="relative bg-background-card rounded-b-lg overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 relative">
                <div className="absolute top-0 right-0 h-full w-full rounded-full border-4 border-t-primary border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                <div className="absolute top-1 right-1 h-12 w-12 rounded-full border-4 border-t-transparent border-r-primary-light border-b-transparent border-l-transparent animate-spin-slow"></div>
              </div>
              <p className="mt-4 text-text-muted-dark animate-pulse">Loading tenant data...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center text-center p-6">
              <div className="rounded-full bg-red-500/10 p-3 mb-4">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Tenant</h3>
              <p className="text-text-muted-dark mb-4">{error}</p>
              <Button 
                variant="outline" 
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="max-h-[80vh] overflow-y-auto px-1 py-2 bg-background-card custom-scrollbar">
              <MultiStepTenantForm
                tenant={tenant}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                isEditMode={isEditMode}
                initialLevel={initialLevel}
                initialType={initialType}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default TenantFormModal; 